import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react"
import BinaryRule, {
  availableRefinementTypes,
  isCodeSigningIdentifier,
  isRefinable,
} from "./binary_rule"
import {
  BinaryFormContext,
  PRIMARY_OPTIONS,
  PRIMARY_TYPES,
  REFINEMENT_TYPES,
  RULES_FIELD,
} from "./models"
import type {
  BinaryRestriction,
  BinaryRuleData,
  BinaryRuleItem,
  Mode,
} from "./models"

interface BinaryRuleListProps {
  data: BinaryRestriction
}

function newRule(key: number): BinaryRuleItem {
  return {
    key,
    displayName: "",
    bundleId: "",
    cdhash: "",
    signingId: "",
    teamId: "",
    pathPrefix: "",
    signingStates: [],
    primaryType: "",
    refinementTypes: [],
    errors: {},
  }
}

function ruleFromData(
  data: BinaryRuleData,
  key: number,
  mode: Mode,
): BinaryRuleItem {
  const primaryType =
    PRIMARY_TYPES.find(type => type === data.primaryType) ||
    PRIMARY_TYPES.find(type => data[type]) ||
    ""
  const rule: BinaryRuleItem = {
    ...newRule(key),
    displayName: data.displayName || "",
    primaryType,
    errors: data.errors || {},
  }

  if (primaryType) {
    rule[primaryType] = data[primaryType] || ""
  }

  if (isCodeSigningIdentifier(primaryType)) {
    rule.signingStates = data.signingStates || []
  }

  if (isRefinable(primaryType)) {
    rule.refinementTypes = availableRefinementTypes(rule).filter(
      type => !!data[type],
    )
    rule.refinementTypes.forEach(type => {
      rule[type] = data[type] || ""
    })
  }

  return demoteInvalidPrimary(rule, mode)
}

// A primary identifier that isn't valid in the newly selected mode becomes a
// refinement, so the admin is prompted to choose a valid one rather than
// silently shipping an invalid rule.
function demoteInvalidPrimary(
  rule: BinaryRuleItem,
  mode: Mode,
): BinaryRuleItem {
  const { primaryType } = rule
  if (!primaryType || PRIMARY_OPTIONS[mode].includes(primaryType)) {
    return rule
  }

  const demotedRule: BinaryRuleItem = { ...rule, primaryType: "" }
  const refinementType = REFINEMENT_TYPES.find(type => type === primaryType)

  if (refinementType) {
    demotedRule.refinementTypes = [refinementType, ...rule.refinementTypes]
  } else {
    demotedRule[primaryType] = ""
  }

  return demotedRule
}

export default function BinaryRuleList({ data }: BinaryRuleListProps) {
  const { mode, disabled } = useContext(BinaryFormContext)
  const nextKey = useRef(0)
  const [rules, setRules] = useState<BinaryRuleItem[]>(() =>
    data.binaryRules.map(rule => ruleFromData(rule, nextKey.current++, mode)),
  )

  useEffect(() => {
    setRules(current => current.map(rule => demoteInvalidPrimary(rule, mode)))
  }, [mode])

  const addRule = useCallback(() => {
    setRules(current => [...current, newRule(nextKey.current++)])
  }, [])

  const updateRule = useCallback((updated: BinaryRuleItem) => {
    setRules(current =>
      current.map(rule => (rule.key === updated.key ? updated : rule)),
    )
  }, [])

  const removeRule = useCallback((key: number) => {
    setRules(current => current.filter(rule => rule.key !== key))
  }, [])

  return (
    <div className="binary-rule-list">
      {rules.length === 0 && (
        <>
          <input type="hidden" name={`${RULES_FIELD}[]`} value="" />
          <div className="binary-rules-empty alert alert-info">
            No binary rules yet. Add a rule to begin.
          </div>
        </>
      )}

      {rules.map((rule, index) => (
        <BinaryRule
          key={rule.key}
          rule={rule}
          index={index}
          onChange={updateRule}
          onRemove={() => removeRule(rule.key)}
        />
      ))}

      {!disabled && (
        <button
          type="button"
          className="btn btn-default binary-rule-add"
          onClick={addRule}
        >
          <span className="glyphicon glyphicon-plus" /> Add binary rule
        </button>
      )}
    </div>
  )
}
